import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';

// Services
import { InputsService } from '@app/inputs/services/inputs.service';
import { AuthService } from '@app/shared/services/auth.service';
import { TranslateService } from '@ngx-translate/core';

// For typeahead
import { TypeaheadSearchFunction } from '@app/shared/shared-api.model';
import { NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap/typeahead/typeahead';
import { BehaviorSubject, Observable, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';

// The currently know list of parameter types that can come from the backend
type ReportParameterType = 'InputIdentifier' | 'InputIdentifierList';

export interface ReportParameter {
  key: string;
  type: ReportParameterType;

  // type-specific methods, bound on input change
  searchHandler?: TypeaheadSearchFunction<string, any>;
  formatter?: (i: any) => string;
}

export interface ReportParameterUpdateEvent extends ReportParameter {
  value: any;
  isMultiSelect?: boolean;
}

@Component({
  selector: 'dgx-reporting-create-report-parameters',
  templateUrl: './reporting-create-report-parameters.component.html',
  styleUrls: ['./reporting-create-report-parameters.component.scss'],
  // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportingCreateReportParametersComponent implements OnChanges {
  private static readonly MaxSelectedCount = 5;

  @Input() public parameters: ReportParameter[] = [];
  @Output()
  public update: EventEmitter<ReportParameterUpdateEvent> = new EventEmitter();

  public i18n = this.translate.instant([
    'dgDownloadableReports_Parameters_InputIdentifier_Name',
    'dgDownloadableReports_Parameters_InputIdentifier_Placeholder',
    'dgDownloadableReports_Parameters_InputIdentifierList_Name',
    'dgDownloadableReports_Parameters_InputIdentifierList_Placeholder',
  ]);

  public modelMap = {};
  public selectionMap = {};
  public isSearchingMap = {};
  public isDisabledMap = {};

  constructor(
    private authService: AuthService,
    private translate: TranslateService,
    private inputsService: InputsService
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    // HACK: bind typeahead methods to the param on change
    if (changes.parameters.currentValue) {
      for (const p of this.parameters) {
        p.searchHandler = this.searchHandlerForType(p.key, p.type);
        p.formatter = this.formatterForType(p.type);
      }
    }
  }

  public trackByKey(index: number, param: ReportParameter): string {
    return param.key;
  }

  public cancelSearch(key: string) {
    this.isSearchingMap[key] = false;
  }

  // Add an item to the selection for a parameter
  public handleSelect(
    key: string,
    $event: NgbTypeaheadSelectItemEvent,
    isMultiSelect?: boolean
  ) {
    const { item } = $event;
    let selected;

    if (!isMultiSelect) {
      selected = this.selectionMap[key] = item;
    } else {
      selected = this.selectionMap[key];
      if (!selected) {
        selected = this.selectionMap[key] = [];
      }
      selected.push($event.item);

      this.checkSelectionExceedsLimit(key);
    }

    // reset the local model and emit
    this.modelMap[key] = '';
    const type = this.typeFromKey(key);
    this.update.emit({ key, type, value: selected, isMultiSelect });
  }

  // @TODO: TBD if this is used. We might be able to remove all this functionatliy.
  // Remove an item from the selection for a parameter
  public handleRemove(key: string, item: any) {
    let selected;
    if (Array.isArray(this.selectionMap[key])) {
      selected = this.selectionMap[key].filter((i) => i !== item);
      this.selectionMap[key] = selected;
      this.checkSelectionExceedsLimit(key);
    } else {
      this.selectionMap[key] = selected;
    }

    const type = this.typeFromKey(key);
    this.update.emit({ key, type, value: selected });
  }

  private searchHandlerForType(
    key: string,
    paramType: ReportParameterType
  ): TypeaheadSearchFunction<string, any> {
    switch (paramType) {
      case 'InputIdentifier':
      case 'InputIdentifierList':
        return this.inputsSearchHandler(key, paramType);
      default:
        console.warn(
          'Search handler not found for parameter type: ' + paramType
        );
    }
  }

  // Formatter for deriving a string value for the model on selecting an item from the typeahead
  private formatterForType(paramType: ReportParameterType): (i: any) => string {
    switch (paramType) {
      case 'InputIdentifier':
      case 'InputIdentifierList':
        return (i) => i.title;
      default:
        return (i) => i.name || '';
    }
  }

  // Handle searches for input type parameters
  private inputsSearchHandler(key: string, paramType: string) {
    return (term: Observable<string>): Observable<readonly any[]> =>
      term.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter((term) => {
          return term.length >= 2;
        }),
        switchMap((term: string) => {
          // Update search state
          this.isSearchingMap[key] = true;

          // Build up exclusion list from the current selection
          const selected = this.selectionMap[key];
          let exclusionList = [];
          if (Array.isArray(selected)) {
            exclusionList = selected.map((input) => ({
              InputId: input.inputId,
              InputType: input.inputType,
            }));
          } else if (selected) {
            exclusionList.push({
              InputId: selected.inputId,
              InputType: selected.inputType,
            });
          }

          // Restrict results to org
          const internalFacet = {
            Id: 'Internal',
            Values: [this.authService.authUser.defaultOrgId],
          };

          // Run the search
          return this.inputsService.find({
            terms: term,
            tags: [],
            facets: [internalFacet],
            skip: 0,
            count: 20,
            includeProviders: true,
            defaults: 'Library',
            includeCompleted: true,
            sortBy: 'DateUpdated',
            isAscending: true,
            exclusionList,
          });
        }),
        map((results) => results.inputs),
        catchError((error) => {
          // for now, swallow error and return no results
          return of([]);
        }),
        // Update search state
        tap(() => (this.isSearchingMap[key] = false))
      );
  }

  private typeFromKey(key: string) {
    return this.parameters.find((p) => p.key === key)?.type;
  }

  // Check if the selected count is over the limit
  private checkSelectionExceedsLimit(key: string) {
    const selected = this.selectionMap[key];
    this.isDisabledMap[key] =
      selected.length >=
      ReportingCreateReportParametersComponent.MaxSelectedCount;
  }
}
